export const humanizeCount = (count: number, singular: string, plural: string) => {
  switch (count) {
    case 0:
      return `No ${plural}`;
    case 1:
      return `1 ${singular}`;
    default:
      return `${count} ${plural}`;
  }
};

export const exportCsv = (fileName: string, csv) => {
  const anchor = document.createElement('a');
  anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  anchor.target = '_blank';
  anchor.download = `${fileName}.csv`;
  anchor.click();
};

export const removeHTMLTags = (html: string) => {
  return html?.replace(/<[^>]*>?/gm, '');
};

export const constructOrderLink = (storeDomain: string, orderId: string, platformId: string) => {
  switch (platformId) {
    case 'shopify':
      return `https://${storeDomain}/admin/orders/${orderId}`;
    case 'bigcommerce':
      return `https://store-${storeDomain}/manage/orders/${orderId}`;
    default:
      return '';
  }
};

export const isShopify = platformId => {
  return platformId === 'shopify';
};

export const isBigcommerce = platformId => {
  return platformId === 'bigcommerce';
};

function toNumOrNull(value: string | number | null) {
  if (value === undefined || value === null) {
    return null;
  }

  return Number(value);
}

export function formatLongNumber(number: number) {
  return number.toLocaleString('en-US', {
    maximumFractionDigits: 2,
  });
}

export function transformPriceRules(rules: any[] = []) {
  return rules.map(rule => {
    return {
      brandCalcMethod: rule.terms.brand?.type || null,
      brandPrice: toNumOrNull(rule.terms.brand?.amount),
      customerCalcMethod: rule.terms.customer?.type || null,
      customerPrice: toNumOrNull(rule.terms.customer?.amount) || null,
      hybridOperator: rule.terms.operator?.toUpperCase() || null,
      id: rule.id || undefined,
      maxValue: parseInt(rule.max), // these have to be defined so no null
      minValue: parseInt(rule.min),
      amountPerResolution: rule.amountPerResolution ?? false,
    };
  });
}

export const typedArrayToBuffer = (array: Uint8Array): ArrayBuffer => {
  return array.buffer.slice(array.byteOffset, array.byteLength + array.byteOffset);
};

/**
 * Converts an ISO 8601 duration string to a human-readable format
 * @param {string} isoDuration - ISO 8601 duration string (e.g., "P1Y2M3DT4H5M6S")
 * @returns {string} Human-readable duration (e.g., "1 Year 2 Months 3 Days 4 Hours 5 Minutes 6 Seconds")
 */
export function formatDuration(isoDuration: string, short = false): string {
  const regex = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?/;
  const matches = isoDuration.match(regex);

  if (!matches) return 'Invalid duration format';

  const [, yearsStr, monthsStr, daysStr] = matches;

  const unitMap = {
    years: { value: parseInt(yearsStr || '0'), label: short ? 'Yr' : 'Year' },
    months: { value: parseInt(monthsStr || '0'), label: short ? 'Mo' : 'Month' },
    days: { value: parseInt(daysStr || '0'), label: 'Day' },
  };

  const pluralize = (count: number, label: string) => `${count} ${label}${count === 1 || short ? '' : 's'}`;

  const result: string[] = [];

  if (unitMap.years.value > 0) {
    result.push(pluralize(unitMap.years.value, unitMap.years.label));
    if (unitMap.months.value > 0) {
      result.push(pluralize(unitMap.months.value, unitMap.months.label));
    }
  } else if (unitMap.months.value > 0) {
    result.push(pluralize(unitMap.months.value, unitMap.months.label));
    if (unitMap.days.value > 0) {
      result.push(pluralize(unitMap.days.value, unitMap.days.label));
    }
  } else if (unitMap.days.value > 0) {
    result.push(pluralize(unitMap.days.value, unitMap.days.label));
  }

  return result.length ? result.join(' ') : '0 Days';
}
